import React from 'react';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Box, Drawer, IconButton, List, ListItemButton, ListItemText, Tooltip } from '@mui/material';
import { ReactComponent as KindergartensIcon } from '../assets/kindergarten-icon.svg';
import { ReactComponent as InfoboardIcon } from '../assets/infoboard-icon.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import { INFOBOARD, KINDERGARTENS } from '../common/constants/routes.constants';
import { useSidebarStore, useSidebarWidth } from '../store/useSidebarStore';
import { useTranslation } from 'react-i18next';
import LanguageSwitch from './LanguageSwitch';
import SidebarLogo from './SidebarLogo';
import SidebarAccount from './SidebarAccount';
import SidebarFinanceGroup, { COLLAPSED_ITEM_SX, SidebarFinanceMenu, SIDEBAR_FOCUS_SX } from './SidebarFinanceGroup';
import { useFinanceNavVisible } from '../pages/Finance/useFinanceNavVisible';

import { palette } from '../theme';
const menuItems = [
  {
    labelKey: 'nav.kindergartens',
    route: KINDERGARTENS,
    icon: <KindergartensIcon style={{ width: 24, height: 24 }} />
  },
  {
    labelKey: 'nav.infoboard',
    route: INFOBOARD,
    icon: <InfoboardIcon style={{ width: 24, height: 24 }} />
  }
];

const TOGGLE_ICON_SX = { fontSize: 20, color: palette.sidebar.textMuted } as const;
const TOGGLE_SX = {
  borderRadius: '8px',
  '&:hover': { backgroundColor: palette.sidebar.selected, '& .MuiSvgIcon-root': { color: palette.common.white } },
  ...SIDEBAR_FOCUS_SX
} as const;

/** Separates "what am I looking at" from "who am I and in which language". */
const FooterDivider: React.FC = () => <Box aria-hidden sx={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.12)', mx: 1 }} />;

/**
 * The app's navigation, in one of two widths: the full list, or a 72px strip of icons the
 * admin collapses it to by hand for more room. Nothing collapses it automatically - not
 * the screen width, not a shortcut - so it is always where the admin left it.
 *
 * The brand sits at the top and the account at the foot, the way most apps arrange it: the
 * top of a sidebar is what you look at to know where you are, not who you are.
 */
const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const showFinance = useFinanceNavVisible();
  const collapsed = useSidebarStore(state => state.collapsed);
  const toggleCollapsed = useSidebarStore(state => state.toggleCollapsed);
  const width = useSidebarWidth();

  // Exact match would leave the item unhighlighted on a nested page such as
  // /infoboard/post/<id>. KINDERGARTENS is '/', so it stays exact.
  const isSelected = (route: string) =>
    route === KINDERGARTENS
      ? location.pathname === KINDERGARTENS
      : location.pathname === route || location.pathname.startsWith(`${route}/`);

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        '& .MuiDrawer-paper': { width, transition: 'width 200ms ease', overflowX: 'hidden' }
      }}
    >
      {collapsed ? (
        <Box sx={{ p: '16px 12px 24px', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <SidebarLogo collapsed />
          <IconButton
            onClick={toggleCollapsed}
            aria-label={t('nav.expand')}
            aria-expanded={false}
            sx={{ ...TOGGLE_SX, width: 48, height: 48, mb: 1 }}
          >
            <KeyboardDoubleArrowRightIcon sx={TOGGLE_ICON_SX} />
          </IconButton>

          {menuItems.map(({ labelKey, route, icon }) => (
            <Tooltip key={route} title={t(labelKey)} placement="right">
              <ListItemButton onClick={() => navigate(route)} selected={isSelected(route)} aria-label={t(labelKey)} sx={COLLAPSED_ITEM_SX}>
                {icon}
              </ListItemButton>
            </Tooltip>
          ))}
          {showFinance && <SidebarFinanceMenu />}

          <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <LanguageSwitch collapsed />
            <SidebarAccount collapsed />
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 40, mb: 4 }}>
            <SidebarLogo />
            <IconButton onClick={toggleCollapsed} aria-label={t('nav.collapse')} aria-expanded sx={{ ...TOGGLE_SX, width: 36, height: 36 }}>
              <KeyboardDoubleArrowLeftIcon sx={TOGGLE_ICON_SX} />
            </IconButton>
          </Box>

          <List>
            {menuItems.map(({ labelKey, route, icon }) => (
              <ListItemButton key={route} onClick={() => navigate(route)} selected={isSelected(route)} sx={SIDEBAR_FOCUS_SX}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {icon}
                  <ListItemText
                    primary={t(labelKey)}
                    slotProps={{
                      primary: {
                        sx: { color: 'white' }
                      }
                    }}
                  />
                </Box>
              </ListItemButton>
            ))}
            {/* hidden when no kindergarten of this admin invoices; see useFinanceNavVisible */}
            {showFinance && <SidebarFinanceGroup />}
          </List>

          <Box sx={{ mt: 'auto' }}>
            <LanguageSwitch />
            <FooterDivider />
            <SidebarAccount />
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;
